import { type FC, useMemo, useRef, useState } from 'react';
import { EmptyFilterState } from './FilterBadge';
import { useStatisticsData } from '@/pages/statistics/StatisticsDataContext';

interface TooltipInfo {
  x: number;
  y: number;
  a: number;
  b: number;
  count: number;
}

const PairFrequencyHeatmap: FC = () => {
  const { filteredDraws, loading } = useStatisticsData();
  const draws = filteredDraws;
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Build 45x45 pair frequency matrix
  const { matrix, maxFreq, totalPairs } = useMemo(() => {
    const m: number[][] = Array.from({ length: 45 }, () => Array(45).fill(0));
    let max = 0;

    draws.forEach((draw) => {
      const nums = draw.numbers;
      for (let i = 0; i < nums.length; i++) {
        for (let j = i + 1; j < nums.length; j++) {
          const a = nums[i] - 1; // 0-indexed
          const b = nums[j] - 1;
          m[a][b]++;
          m[b][a]++;
          if (m[a][b] > max) max = m[a][b];
        }
      }
    });

    const total = draws.length * 15; // each draw has 15 pairs (6C2)
    return { matrix: m, maxFreq: max, totalPairs: total };
  }, [draws]);

  const getCellColor = (freq: number): string => {
    if (freq === 0) return 'bg-background-100';
    const ratio = freq / maxFreq;
    if (ratio >= 0.85) return 'bg-accent-500';
    if (ratio >= 0.7) return 'bg-accent-400';
    if (ratio >= 0.55) return 'bg-accent-300';
    if (ratio >= 0.4) return 'bg-accent-200';
    if (ratio >= 0.25) return 'bg-accent-100';
    return 'bg-accent-50';
  };

  const handleCellEnter = (e: React.MouseEvent, row: number, col: number, count: number) => {
    if (count === 0) {
      setTooltip(null);
      return;
    }
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    setTooltip({
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top - 8,
      a: row + 1,
      b: col + 1,
      count,
    });
    setTooltipPos({
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top - 8,
    });
  };

  const handleCellLeave = () => {
    setTooltip(null);
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-background-200 rounded w-40 mb-4"></div>
        <div className="h-[340px] bg-background-200 rounded-xl"></div>
      </div>
    );
  }

  if (!draws.length) {
    return <EmptyFilterState />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <p className="text-xs text-foreground-400">
          총 {draws.length}회차 기준, 두 번호의 동시 출현 빈도를 히트맵으로 보여줍니다. 진할수록 자주 함께 나온 쌍이에요.
        </p>
        <span className="text-xs font-mono text-foreground-300">
          최대 {maxFreq}회
        </span>
      </div>

      {/* Heatmap container */}
      <div
        ref={containerRef}
        className="relative overflow-auto border border-background-200/60 rounded-xl"
        style={{ maxHeight: '520px' }}
      >
        {/* Column headers */}
        <div className="sticky top-0 z-10 flex bg-background-50 border-b border-background-200/60">
          {/* Corner spacer */}
          <div className="flex-shrink-0 w-8 h-6 flex items-end justify-center bg-background-50"></div>
          {Array.from({ length: 45 }, (_, i) => (
            <div
              key={`col-${i}`}
              className="flex-shrink-0 w-[14px] h-6 flex items-end justify-center bg-background-50"
            >
              <span className="text-[8px] text-foreground-300 leading-none pb-0.5">
                {i + 1}
              </span>
            </div>
          ))}
        </div>

        {/* Grid rows */}
        <div className="relative">
          {Array.from({ length: 45 }, (_, row) => (
            <div key={`row-${row}`} className="flex">
              {/* Row header */}
              <div className="flex-shrink-0 w-8 h-[14px] flex items-center justify-end pr-1 bg-background-50 border-r border-background-200/60">
                <span className="text-[8px] text-foreground-400 leading-none">
                  {row + 1}
                </span>
              </div>
              {/* Cells */}
              {Array.from({ length: 45 }, (_, col) => {
                const freq = matrix[row][col];
                const isDiagonal = row === col;
                const isLowerTriangle = row > col;

                if (isDiagonal) {
                  return (
                    <div
                      key={`cell-${row}-${col}`}
                      className="flex-shrink-0 w-[14px] h-[14px] bg-background-50"
                    />
                  );
                }

                // Upper triangle: mirror of lower, show same color
                if (!isLowerTriangle) {
                  return (
                    <div
                      key={`cell-${row}-${col}`}
                      className={`flex-shrink-0 w-[14px] h-[14px] ${getCellColor(freq)} cursor-pointer`}
                      onMouseEnter={(e) => handleCellEnter(e, row, col, freq)}
                      onMouseLeave={handleCellLeave}
                      title={`${col + 1}-${row + 1}: ${freq}회`}
                    />
                  );
                }

                // Lower triangle
                return (
                  <div
                    key={`cell-${row}-${col}`}
                    className={`flex-shrink-0 w-[14px] h-[14px] ${getCellColor(freq)} cursor-pointer`}
                    onMouseEnter={(e) => handleCellEnter(e, row, col, freq)}
                    onMouseLeave={handleCellLeave}
                    title={`${col + 1}-${row + 1}: ${freq}회`}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute z-20 pointer-events-none bg-foreground-900 text-white text-[10px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg -translate-x-1/2 -translate-y-full"
            style={{
              left: `${tooltipPos.x}px`,
              top: `${tooltipPos.y}px`,
            }}
          >
            <span className="font-bold">{tooltip.a}</span>
            <span className="mx-0.5 text-foreground-400">+</span>
            <span className="font-bold">{tooltip.b}</span>
            <span className="mx-1">→</span>
            <span className="text-accent-300 font-bold">{tooltip.count}회</span>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-background-200/60">
        <span className="text-[10px] text-foreground-400">낮음</span>
        <div className="flex items-center gap-0.5">
          {['bg-accent-50', 'bg-accent-100', 'bg-accent-200', 'bg-accent-300', 'bg-accent-400', 'bg-accent-500'].map((cls, i) => (
            <div key={i} className={`w-4 h-3 rounded-sm ${cls}`} title={`Level ${i + 1}`} />
          ))}
        </div>
        <span className="text-[10px] text-foreground-400">높음</span>
        <span className="text-[10px] text-foreground-300 ml-auto">
          전체 쌍 조합: 990개 | 총 출현: {totalPairs}회
        </span>
      </div>
    </div>
  );
};

export default PairFrequencyHeatmap;